import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material"
import { useCallback, useContext, useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { MainContext } from "../../controllers/main"
import { MarketplaceContext } from "../../controllers/marketplace"
import { MarketplaceCodesContext } from "../../controllers/marketplaceCodes"
import {
  calculateTimeElapsed,
  registerAnalyticsEvent,
} from "../../services/utilities/utility"
import EditHeading from "../global/editHeading"
import ListContainer from "../global/listContainer"
import TableSpinner from "../global/tableSpinner"
import ListTable from "../global/listTable"
import ProductCode from "../../models/productCode"
import TitleTableCell from "../global/titleTableCell"
import PrimaryBadge from "../global/primaryBadge"
import {
  DeleteRounded,
  Inventory2Rounded,
  LocalGroceryStoreRounded,
} from "@mui/icons-material"
import { ProductAvailability } from "../../services/config/enum"
import { LoadingButton } from "@mui/lab"
import CreateProductCodeDialog from "./createProductCodeDialog"

const ProductCodesList = () => {
  const { setAnimation, showPadding } = useContext(MainContext)
  const { currentProduct, getCurrentProduct } = useContext(MarketplaceContext)
  const {
    loading,
    codesList,
    setCodesList,
    getCodesList,
    codesListNextToken,
    loadMoreCodes,
    deleteCode,
  } = useContext(MarketplaceCodesContext)
  const navigate = useNavigate()
  const { productId } = useParams()

  // get codes list and current product if not present
  const [loadingCurrentProduct, setLoadingCurrentProduct] =
    useState<boolean>(true)

  useEffect(() => {
    const getProduct = async () => {
      await getCurrentProduct(productId)
      setLoadingCurrentProduct(false)
    }

    getCodesList(productId)

    if (!currentProduct) {
      getProduct()
    } else {
      setLoadingCurrentProduct(false)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // handle create product quiz dialog
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)

  const handleDialogChange = useCallback(() => {
    setDialogOpen(!dialogOpen)
  }, [dialogOpen])

  // deleted logic
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
  const [codeToDelete, setCodeToDelete] = useState<ProductCode>()
  const [deleting, setDeleting] = useState<boolean>(false)
  const [deletedFeedbackOpen, setDeletedFeedbackOpen] = useState<boolean>(false)

  // load more button loading
  const [loadMoreLoading, setLoadMoreLoading] = useState<boolean>(false)

  // register analytics event
  useEffect(() => {
    registerAnalyticsEvent({ type: "page_view", name: "product_codes_list" })
  }, [])

  return (
    <Stack style={{ paddingTop: 1 }}>
      <Stack direction="row" alignItems="flex-end" style={{ paddingRight: 16 }}>
        <EditHeading
          backButtonOnClick={() => {
            const pathname = window.location.pathname

            setAnimation(false)
            setTimeout(() => {
              navigate(pathname.slice(0, pathname.indexOf("/codes")))
            }, 300)
          }}
          title={
            currentProduct && currentProduct.document
              ? `${
                  currentProduct.document.items.find((item) => item.isDefault)
                    .title
                } / Codes`
              : "Codes"
          }
          titleLoading={loadingCurrentProduct}
          showCustomButton
          customButtonOnClick={handleDialogChange}
          customButtonLabel="Add Codes"
        />
      </Stack>
      <ListContainer style={{ position: "relative" }}>
        {loading ? (
          <TableSpinner
            height={
              showPadding === "yes"
                ? "calc(100vh - 149px)"
                : "calc(100vh - 116px)"
            }
          />
        ) : (
          <ListTable
            height={
              showPadding === "yes"
                ? "calc(100vh - 149px)"
                : "calc(100vh - 116px)"
            }
            headingItems={[
              "Code & ID",
              "Available",
              "Bought",
              "Inventory",
              "Updated",
              "",
            ]}
            nextToken={codesListNextToken}
            loadingMoreItems={loadMoreLoading}
            setLoadingMoreItems={setLoadMoreLoading}
            loadMore={() => loadMoreCodes(productId)}
            tableBody={codesList.map((item: ProductCode) => (
              <TableRow
                key={item.itemId}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              >
                <TitleTableCell title={item.code} id={item.itemId} />
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      item.availability === ProductAvailability.Limited ||
                      item.inventoryCount > 0
                        ? "✔"
                        : "✗"
                    }
                    color={
                      item.availability === ProductAvailability.Limited ||
                      item.inventoryCount > 0
                        ? "success"
                        : "default"
                    }
                  />
                </TableCell>
                <TableCell>
                  <PrimaryBadge
                    count={item.boughtCount ?? 0}
                    icon={<LocalGroceryStoreRounded style={{ fontSize: 12 }} />}
                  />
                </TableCell>
                <TableCell>
                  <PrimaryBadge
                    count={
                      !item.inventoryCount &&
                      item.availability === ProductAvailability.Limited
                        ? 0
                        : item.inventoryCount ?? "∞"
                    }
                    icon={<Inventory2Rounded style={{ fontSize: 12 }} />}
                  />
                </TableCell>
                <TableCell style={{ minWidth: 110 }}>
                  <Typography variant="caption">
                    {calculateTimeElapsed(item.updatedAt)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {!item.boughtCount ? (
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        setCodeToDelete(item)
                        setDeleteDialogOpen(true)
                      }}
                    >
                      <DeleteRounded color="error" />
                    </IconButton>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          />
        )}
        <CreateProductCodeDialog
          dialogOpen={dialogOpen}
          setDialogOpen={setDialogOpen}
          handleDialogChange={handleDialogChange}
        />
        {/* delete dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={deleting ? null : () => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Are you sure you want to delete this Code?
          </DialogTitle>
          <DialogContent>
            {codeToDelete ? (
              <DialogContentText id="delete-dialog-description">
                Code {codeToDelete.code} with ID {codeToDelete.itemId} will be
                permanently deleted
              </DialogContentText>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={async () => {
                setDeleting(true)

                const deletedCodeId = await deleteCode(
                  currentProduct.id,
                  codeToDelete.itemId
                )

                if (deletedCodeId) {
                  // remove code from list locally
                  const deletedCodeIndex = codesList.findIndex(
                    (code) => code.itemId === deletedCodeId
                  )
                  if (deletedCodeIndex >= 0) {
                    setCodesList((current) => [
                      ...current.slice(0, deletedCodeIndex),
                      ...current.slice(deletedCodeIndex + 1),
                    ])
                  }

                  setDeleteDialogOpen(false)
                  setDeletedFeedbackOpen(true)
                }

                setDeleting(false)
              }}
              loading={deleting}
              autoFocus
              color="error"
            >
              Delete
            </LoadingButton>
          </DialogActions>
        </Dialog>
        {/* delete feedback */}
        <Snackbar
          open={deletedFeedbackOpen}
          onClose={() => {
            setDeletedFeedbackOpen(false)
          }}
          autoHideDuration={3000}
        >
          <Alert severity="success">Code deleted</Alert>
        </Snackbar>
      </ListContainer>
    </Stack>
  )
}

export default ProductCodesList
